/**
 * 自前の線画のアイコン（受け取った書類 S3＝../eigyouryoku/docs/13 §4.5、絵＝screen_sample/design_2026-10-11/received-read/Parts）。
 *   色は currentColor（置いた所の文字の色）。線 1.75（24px は 1.6・32px は 1.4＝大きくしても線が太りすぎない）。緑・黄は使わない
 *   ScanIcon＝書類の輪郭＋走査線（AI読み取り）。走査線は書類の外へ少しはみ出す＝読み取っている印。
 *     moving＝走査線だけが上下に動く（AI読み取り中。@keyframes rdscan＝styles.css。prefers-reduced-motion で止めて中央に置く）
 *   CalendarIcon＝日付の欄の右端（DateInput）
 *   BuildingIcon・SearchIcon・ClearIcon・UpDownIcon＝「会社情報取得」（2026-10-14、Figma「会社情報取得（10/14）」の 建物・虫眼鏡・消す・上下）
 *   BellIcon＝左の列・☰ の「お知らせ」（#206、docs/83 §14）
 */
export function ScanIcon({ size = 18, moving = false, className, testId }: { size?: 18 | 22 | 24 | 32; moving?: boolean; className?: string; testId?: string }) {
  const sw = size >= 32 ? 1.4 : size >= 24 ? 1.6 : 1.75;
  return (
    <svg
      className={"scanico" + (moving ? " is-moving" : "") + (className ? ` ${className}` : "")}
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 2.5h7l4 4V17.5H5z" />
      <path d="M12 2.5v4h4" />
      <path d="M8 11h6M8 14h4" />
      {/* 走査線：止まっているときは y=7.5（上の方）。動くときは 5.5 から 8px 下へ（CSS の translateY）。動きを減らすと中央 */}
      <rect className="scanico-ln" x="3" y={moving ? 5.5 : 7.5} width="14" height="1.6" rx=".8" fill="currentColor" stroke="none" data-testid={testId} />
    </svg>
  );
}

export function CalendarIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <rect x="3" y="4" width="14" height="13" rx="2" />
      <path d="M3 8h14M7 2.5v3M13 2.5v3" />
    </svg>
  );
}

/** 情報のしるし（ⓘ。注意書きの頭）。filled＝塗りの丸に抜きの i（#287 新しい会社の作成の藍の帯） */
export function InfoIcon({ size = 16, filled = false }: { size?: number; filled?: boolean }) {
  if (filled) {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" strokeLinecap="round">
        <circle cx="8" cy="8" r="7.5" fill="currentColor" />
        <path d="M8 7v4.2M8 4.6v.1" fill="none" stroke="var(--c-on-primary)" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7v4M8 5v.1" />
    </svg>
  );
}

/** 新しいタブで開く（原本を開く） */
export function OpenIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3H3v10h10v-3M9 3h4v4M13 3 7 9" />
    </svg>
  );
}

/** 回る丸（読み取り中の引き出し。.sti.spin と同じ形。prefers-reduced-motion で止まる） */
export function SpinIcon({ size = 22 }: { size?: number }) {
  return (
    <svg className="sti spin" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity=".25" strokeWidth="2.2" />
      <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** 点線の丸（順番待ち。{vendorQuote}の待機中と同じ） */
export function QueuedIcon({ size = 16 }: { size?: number }) {
  return (
    <svg className="sti" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2.2 2.2" />
    </svg>
  );
}

/** 丸に ✓（取込済。藍） */
export function DoneIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="8" />
      <path d="M6.5 10.3l2.4 2.4 4.8-5" />
    </svg>
  );
}

/**
 * 幅に合わせる（S4、docs/13 §4.6 ②）：両端の縦線と内向きの矢印。原本のツールバーの 36px のボタンに入れる（言葉は aria-label と title）
 */
export function FitWidthIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4v12M17 4v12" />
      <path d="M6 10h8M8 7.5 5.5 10 8 12.5M12 7.5l2.5 2.5-2.5 2.5" />
    </svg>
  );
}

/**
 * 原本を広く／内容を広く（S4 ②）：枠の中の仕切りと矢印。side＝広げる側（original＝仕切りが右へ・content＝仕切りが左へ）
 */
export function WidenIcon({ size = 20, side }: { size?: number; side: "original" | "content" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="4" width="15" height="12" rx="1.5" />
      {side === "original" ? <path d="M9 4v12M12 10h3M13.5 8.5 15 10l-1.5 1.5" /> : <path d="M11 4v12M8 10H5M6.5 8.5 5 10l1.5 1.5" />}
    </svg>
  );
}

/** 注意の三角（⚠。S4 の「差出人が{customer}と異なります」）。色は置いた所の文字の色（朱の文字と一緒に使う＝色だけにしない） */
export function WarnIcon({ size = 18 }: { size?: number }) {
  return (
    <svg className="warnico" width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3 2.5 16.5h15z" />
      <path d="M10 8v4M10 14.2v.1" />
    </svg>
  );
}

/** ✓（検算 一致・選んでいる書類） */
export function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

/** 書類の種類のアイコン（S4、スマホの書類を選ぶシート。絵＝received-read/PhoneSheet）。24px・線 1.6 */
export function KindIcon({ kind, size = 24 }: { kind: "order" | "inquiry" | "invoice" | "drawing" | "photo" | "material" | "other"; size?: number }) {
  const body = {
    order: (
      <>
        <path d="M5 2.5h7l4 4V17.5H5z" />
        <path d="M12 2.5v4h4" />
        <path d="M7.5 10.5h5M7.5 13.5h5" />
      </>
    ),
    inquiry: (
      <>
        <path d="M5 2.5h7l4 4V17.5H5z" />
        <path d="M12 2.5v4h4" />
        <path d="M8.5 12.5a1.5 1.5 0 1 1 1.5 1.5M10 16.3v.1" />
      </>
    ),
    /** 請求書（#233 3a）：紙に ¥ */
    invoice: (
      <>
        <path d="M5 2.5h7l4 4V17.5H5z" />
        <path d="M12 2.5v4h4" />
        <path d="M8 9l2 2.5L12 9M10 11.5v4M8.3 12.8h3.4M8.3 14.4h3.4" />
      </>
    ),
    drawing: (
      <>
        <rect x="3" y="3" width="14" height="14" rx="1.5" />
        <path d="M3 9h14M9 3v14" />
      </>
    ),
    photo: (
      <>
        <rect x="2.5" y="5" width="15" height="11" rx="2" />
        <circle cx="10" cy="10.5" r="3" />
        <path d="M7 5l1-2h4l1 2" />
      </>
    ),
    material: (
      <>
        <path d="M4 3.5h12v13H4z" />
        <path d="M7 7.5h6M7 10.5h6M7 13.5h4" />
      </>
    ),
    other: (
      <>
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6.5v4M10 13.5v.1" />
      </>
    ),
  }[kind];
  return (
    <svg className="kindico" width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {body}
    </svg>
  );
}

/** 建物（「会社情報取得」のボタン。名前の欄の中の右端）。20px・線 1.5 */
export function BuildingIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.33 17.5V4.17c0-.92.75-1.67 1.67-1.67h5.83c.92 0 1.67.75 1.67 1.67V17.5" />
      <path d="M12.5 7.5H15c.92 0 1.67.75 1.67 1.67V17.5" />
      <path d="M2.08 17.5h15.84" />
      <path d="M6.67 6.25h2.5M6.67 9.58h2.5M6.67 12.92h2.5" />
    </svg>
  );
}

/** 虫眼鏡（検索の欄の左）。20px・線 1.67 */
export function SearchIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9.17" cy="9.17" r="5.42" />
      <path d="M13.33 13.33l3.34 3.34" />
    </svg>
  );
}

/** ⓧ（検索の欄の文字を消す）。塗りの丸＋面の色の ×（ライトは白・ダークは暗い面＝#191 の2回目） */
export function ClearIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="9" fill="currentColor" />
      <path d="M6 6l6 6M12 6l-6 6" fill="none" style={{ stroke: "var(--c-surface)" }} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/** 上下（選ぶボタンの右。都道府県の選択）。16px */
export function UpDownIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 6l3-3 3 3M5 10l3 3 3-3" />
    </svg>
  );
}

/** ベル（左の列・☰ の「お知らせ」。#206）。左の列の線の太さ・色は .navhelp svg の CSS（ほかの項目のアイコンと同じ 24 の枠・線 2） */
export function BellIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9.5a6 6 0 0 1 12 0c0 5.5 2.5 7.5 2.5 7.5h-17S6 15 6 9.5" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    </svg>
  );
}
